import Swiper from 'swiper'
import { A11y, Navigation, Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

/** Arrow keys move the selection within the tablist, as a tablist is expected to. */
const KEY_STEP = { ArrowLeft: -1, ArrowRight: 1 }

/**
 * One carousel per year panel.
 *
 * The controls are passed as elements, not selector strings: every panel carries
 * the same `.progress-*` classes, and a string would resolve to the first panel's
 * arrows and dots for all of them — so the 2026 arrows would drive 2027.
 */
function initPanelCarousel(panel, year) {
    new Swiper(panel.querySelector('.progress-images'), {
        modules: [A11y, Navigation, Pagination],
        // Same as the gallery track: two cards fill the container at desktop and
        // the row runs off the right edge of the screen, so the next update is
        // cut by the viewport rather than sized to peek inside the gutter. See
        // base/carousel.css for the unclipped track.
        slidesPerView: 1,
        spaceBetween: 24,
        loop: false,
        speed: 500,
        // Same drag affordance as the gallery track above it — the two rows sit
        // on the same page and behave the same way.
        grabCursor: true,
        breakpoints: {
            640: { slidesPerView: 2 },
        },
        navigation: {
            prevEl: panel.querySelector('.progress-prev'),
            nextEl: panel.querySelector('.progress-next'),
        },
        pagination: {
            el: panel.querySelector('.progress-pagination'),
            clickable: true,
        },
        a11y: { containerMessage: `Site progress, ${year}` },
    })
}

/**
 * Move the selection to `index`.
 *
 * Both halves of the state live in attributes rather than classes, so the
 * styling in base/gallery.css hangs off the same thing a screen reader reads —
 * they can't fall out of step. `tabIndex` is the roving part: the row is one
 * tab stop, and the arrows move within it.
 */
function selectYear(tabs, panels, index, { focus = false } = {}) {
    tabs.forEach((tab, i) => {
        const isSelected = i === index
        tab.setAttribute('aria-selected', String(isSelected))
        tab.tabIndex = isSelected ? 0 : -1
        panels[i].setAttribute('aria-hidden', String(!isSelected))
    })

    if (focus) tabs[index].focus()
}

/**
 * Site progress — a carousel per year, behind a year tablist.
 *
 * The panels are stacked and hidden with `visibility` (see base/gallery.css), so
 * every track is laid out at init and measures itself correctly. Nothing needs a
 * re-measure when a tab is chosen.
 */
export function initSiteProgress() {
    const root = document.querySelector('[data-site-progress]')
    if (!root) return

    const tabs = Array.from(root.querySelectorAll('[role="tab"]'))
    const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')))
    if (panels.some((panel) => !panel)) return

    panels.forEach((panel, i) => initPanelCarousel(panel, tabs[i].textContent.trim()))

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectYear(tabs, panels, index))

        tab.addEventListener('keydown', (event) => {
            const step = KEY_STEP[event.key]
            if (!step) return

            event.preventDefault()
            // Wrap, so neither end of the row is a dead key press.
            const next = (index + step + tabs.length) % tabs.length
            selectYear(tabs, panels, next, { focus: true })
        })
    })
}
